import type { ReactNode } from "react";

// Búsqueda literal sin tildes ni mayúsculas (ChatSearchText del portal actual) y el resaltado <mark class="chat-match">
// como nodos de React: mismo HTML, sin pasar texto de la persona como marcado.

const marks = /\p{Mn}/gu;

/** Texto sin marcas diacríticas y en minúsculas (Fold). */
export function fold(text: string | null | undefined): string {
  return (text ?? "").normalize("NFD").replace(marks, "").normalize("NFC").toLowerCase();
}

/** ¿El texto contiene el término, sin tildes ni mayúsculas? (Contains). Un término vacío siempre coincide. */
export function contains(text: string | null | undefined, term: string | null | undefined): boolean {
  return fold(text).includes(fold(term));
}

const segmenter = typeof Intl !== "undefined" && "Segmenter" in Intl ? new Intl.Segmenter() : null;

/** Partes del texto con las coincidencias marcadas (Highlight): [texto, coincide][]. */
export function highlightParts(text: string | null | undefined, term: string | null | undefined): [string, boolean][] {
  const value = text ?? "";
  if (!term?.trim()) return [[value, false]];
  const needle = fold(term);
  if (!needle) return [[value, false]];
  // Cada letra del texto plegado recuerda de qué elemento del original viene (las tildes no se cuentan).
  let folded = "";
  const starts: number[] = [];
  const ends: number[] = [];
  const elements = segmenter
    ? [...segmenter.segment(value)].map((part) => ({ text: part.segment, index: part.index }))
    : [...value].map((char, index) => ({ text: char, index }));
  let offset = 0;
  for (const element of elements) {
    const index = segmenter ? element.index : offset;
    offset += element.text.length;
    const normalized = fold(element.text);
    folded += normalized;
    for (let i = 0; i < normalized.length; i++) {
      starts.push(index);
      ends.push(index + element.text.length);
    }
  }
  const parts: [string, boolean][] = [];
  let cursor = 0;
  let foldedCursor = 0;
  while (foldedCursor < folded.length) {
    const at = folded.indexOf(needle, foldedCursor);
    if (at < 0) break;
    const begin = starts[at]!;
    const end = ends[at + needle.length - 1]!;
    if (begin > cursor) parts.push([value.slice(cursor, begin), false]);
    parts.push([value.slice(begin, end), true]);
    cursor = end;
    foldedCursor = at + needle.length;
  }
  if (cursor < value.length || parts.length === 0) parts.push([value.slice(cursor), false]);
  return parts;
}

/** El texto con la búsqueda resaltada; sin coincidencia, el texto tal cual. */
export function Highlighted({ text, term }: { text: string | null | undefined; term: string | null | undefined }) {
  const parts = highlightParts(text, term);
  if (parts.length === 1 && !parts[0]![1]) return <>{parts[0]![0]}</>;
  return (
    <>
      {parts.map(([part, match], index): ReactNode =>
        match ? (
          <mark key={index} className="chat-match">
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}
